"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Navbar } from "@/components/layout/navbar";
import { Footer } from "@/components/layout/footer";
import { Button } from "@/components/ui/button";
import { useSearch } from "@/hooks/use-search";
import { Search, Zap, Gauge, Telescope, Loader2 } from "lucide-react";
import { SIMULATION_TIERS, type SimulationTier, SIMULATION_DISCLAIMER } from "@/lib/types/simulation";
import { THESIS_PUBLICATION_DISCLOSURE } from "@/components/simulate/publication-copy";
import { InfoHint } from "@/components/compass/InfoHint";
import { trackEvent } from "@/lib/posthog";

interface SelectedStock {
  ticker: string;
  stockName: string;
  exchange: string;
}

export default function SimulateSetupPage() {
  const { status } = useSession();
  const router = useRouter();

  const [query, setQuery] = useState("");
  const { results, isLoading: searchLoading } = useSearch(query);
  const [selected, setSelected] = useState<SelectedStock | null>(null);
  const [tier, setTier] = useState<SimulationTier>("standard");
  const [thesis, setThesis] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "unauthenticated") {
      router.replace(`/login?callbackUrl=${encodeURIComponent("/simulate/setup")}`);
    }
  }, [status, router]);

  if (status === "loading" || status === "unauthenticated") {
    return (
      <div className="min-h-screen bg-navy">
        <Navbar />
        <div className="flex items-center justify-center pt-32">
          <div className="w-6 h-6 border-2 border-blue/30 border-t-blue rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  async function handleStart() {
    if (!selected || submitting) return;
    setError(null);
    setSubmitting(true);
    trackEvent("simulation_run", {
      ticker: selected.ticker,
      exchange: selected.exchange,
      tier,
    });
    try {
      const res = await fetch("/api/simulate/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ticker: selected.ticker,
          stockName: selected.stockName,
          exchange: selected.exchange,
          tier,
          userThesis: thesis.trim() || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message ?? data.error ?? "Could not start simulation");
        setSubmitting(false);
        return;
      }
      router.push(`/simulate/live/${data.simulationId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start simulation");
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-navy text-foreground">
      <Navbar />

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <h1 className="font-serif text-2xl md:text-3xl font-bold mb-2">Start a new simulation</h1>
        <p className="text-muted-foreground text-sm mb-8">
          Pick a stock, choose a tier, and optionally add your thesis to seed the debate.
        </p>

        {/* Step 1 — Stock */}
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            1. Select a stock
          </h2>
          {selected ? (
            <div className="bg-surface border border-blue/40 rounded-xl p-4 flex items-center justify-between">
              <div>
                <div className="font-semibold">{selected.stockName}</div>
                <div className="text-xs text-muted-foreground">
                  {selected.ticker} · {selected.exchange}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                Change
              </Button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by company name or ticker (e.g., Reliance, AAPL)"
                  className="w-full bg-surface border border-surface-light/30 rounded-lg pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-blue/60"
                  autoFocus
                />
              </div>
              {query.length >= 2 && (
                <div className="mt-2 bg-surface border border-surface-light/30 rounded-lg divide-y divide-surface-light/20 max-h-64 overflow-auto">
                  {searchLoading && (
                    <div className="px-4 py-3 text-xs text-muted-foreground flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" />
                      Searching…
                    </div>
                  )}
                  {!searchLoading && results.length === 0 && (
                    <div className="px-4 py-3 text-xs text-muted-foreground">No results</div>
                  )}
                  {results.map((r) => (
                    <button
                      key={`${r.ticker}-${r.exchange}`}
                      type="button"
                      onClick={() =>
                        setSelected({
                          ticker: r.ticker,
                          stockName: r.companyName,
                          exchange: r.exchange,
                        })
                      }
                      className="w-full text-left px-4 py-2.5 hover:bg-navy-mid transition flex items-center justify-between cursor-pointer bg-transparent border-none"
                    >
                      <div>
                        <div className="text-sm font-medium">{r.companyName}</div>
                        <div className="text-[11px] text-muted-foreground">
                          {r.ticker} · {r.exchange}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </section>

        {/* Step 2 — Tier */}
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
            2. Choose tier
          </h2>
          <div className="grid md:grid-cols-3 gap-3">
            {(["quick_scan", "standard", "deep_dive"] as const).map((t) => {
              const meta = SIMULATION_TIERS[t];
              const Icon = t === "quick_scan" ? Zap : t === "standard" ? Gauge : Telescope;
              const active = tier === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTier(t)}
                  className={`text-left rounded-xl p-4 border-2 transition cursor-pointer ${
                    active
                      ? "border-blue bg-blue/10"
                      : "border-surface-light/30 bg-surface hover:border-surface-light/60"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={16} className={active ? "text-blue" : "text-muted-foreground"} />
                    <div className="font-semibold">{meta.label}</div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mb-2">{meta.description}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {meta.agents} agents · {meta.rounds} rounds · {meta.estimatedTime}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Step 3 — Thesis */}
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <span>
              3. Your thesis{" "}
              <span className="text-[10px] text-muted-foreground normal-case">(optional)</span>
            </span>
            <InfoHint
              label="your thesis"
              text="The agents read it, so the debate they produce can echo your wording. The text you type here is never served to anyone else in any state, published or not."
            />
          </h2>
          <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
            {THESIS_PUBLICATION_DISCLOSURE}
          </p>
          <textarea
            value={thesis}
            onChange={(e) => setThesis(e.target.value)}
            placeholder="Why are you looking at this stock? What do you think the market is missing?"
            rows={4}
            maxLength={1500}
            className="w-full bg-surface border border-surface-light/30 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-blue/60 resize-none"
          />
          <div className="mt-1 text-[10px] text-muted-foreground text-right">
            {thesis.length}/1500
          </div>
        </section>

        {error && (
          <div className="mb-4 text-sm text-cred bg-cred/10 border border-cred/30 rounded-lg p-3">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => router.push("/simulate")}>
            Cancel
          </Button>
          <Button onClick={handleStart} disabled={!selected || submitting}>
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin mr-2" />
                Starting…
              </>
            ) : (
              "Run Simulation"
            )}
          </Button>
        </div>

        <div className="mt-8 text-[11px] text-muted-foreground border border-surface-light/20 rounded-lg p-3 leading-relaxed">
          {SIMULATION_DISCLAIMER}
        </div>
      </main>

      <Footer />
    </div>
  );
}
